import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import * as Sentry from '@sentry/react';
import {
  developerGamesCapabilities,
  GameDetailView,
  GamesProvider,
  type GamesApiAdapter,
  type GamesModule,
} from '@ludolio/shared-games';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

const gamesApi: GamesApiAdapter = {
  getGame: (gameId) => apiClient.getDeveloperGame(gameId),
  updateGame: (gameId, data) => apiClient.updateDeveloperGame(gameId, data),
  submitForReview: (gameId) => apiClient.submitForReview(gameId),
  getAccessibilityCatalog: () => apiClient.getAccessibilityCatalog(),
  getTaxonomyCatalog: () => apiClient.getTaxonomyCatalog(),
  uploadGameMedia: (params) => apiClient.uploadGameMedia(params),
  publishGameBuild: (buildId, channel) => apiClient.publishGameBuild(buildId, channel),
  unpublishGameBuild: (buildId, channel) => apiClient.unpublishGameBuild(buildId, channel),
  initiateMultipartUpload: (params) => apiClient.initiateMultipartUpload(params),
  getPartUploadUrls: (params) => apiClient.getPartUploadUrls(params),
  completeMultipartUpload: (params) => apiClient.completeMultipartUpload(params),
  abortMultipartUpload: (params) => apiClient.abortMultipartUpload(params),
  getGameAchievements: (gameId) => apiClient.getGameAchievements(gameId),
  createAchievement: (data) => apiClient.createAchievement(data),
  updateAchievement: (achievementId, data) => apiClient.updateAchievement(achievementId, data),
  deleteAchievement: (achievementId) => apiClient.deleteAchievement(achievementId),
  uploadAchievementIcon: (params) => apiClient.uploadAchievementIcon(params),
  getGameStats: (gameId) => apiClient.getGameStats(gameId),
  createStat: (data) => apiClient.createStat(data),
  deleteStat: (statId) => apiClient.deleteStat(statId),
};

const reportGameError: GamesModule['reportError'] = (error, context) => {
  Sentry.captureException(error, { extra: { context } });
};

export const Route = createFileRoute('/_authenticated/games/$gameId')({
  component: GameDetailPage,
});

function GameDetailPage() {
  const { gameId } = Route.useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const module = useMemo<GamesModule>(() => ({
    api: gamesApi,
    capabilities: developerGamesCapabilities,
    currentUser: user ? { id: user.id, developerCompanyName: user.developerCompanyName } : null,
    navigateToGames: () => navigate({ to: '/games' }),
    reportError: reportGameError,
  }), [navigate, user?.developerCompanyName, user?.id]);

  return (
    <GamesProvider module={module}>
      <GameDetailView gameId={gameId} />
    </GamesProvider>
  );
}
